<template>
  <a-modal
    v-model:visible="visible"
    title="Editar carga"
    :closable="!loading"
    :maskClosable="!loading"
    @cancel="closeModal"
  >
    <a-spin :spinning="loading">
      <a-form
        ref="formRef"
        layout="vertical"
        :model="formState"
        :rules="rules"
        autocomplete="off"
      >
        <a-form-item label="RUT">
          <a-input v-model:value="formState.rut" disabled />
        </a-form-item>

        <a-form-item label="Nombre" name="firstName">
          <a-input v-model:value="formState.firstName" />
        </a-form-item>

        <a-form-item label="Segundo nombre">
          <a-input v-model:value="formState.secondName" />
        </a-form-item>

        <a-form-item label="Apellido paterno" name="firstSurname">
          <a-input v-model:value="formState.firstSurname" />
        </a-form-item>

        <a-form-item label="Apellido materno">
          <a-input v-model:value="formState.secondSurname" />
        </a-form-item>

        <a-form-item label="Fecha de nacimiento" name="birthdayDate">
          <a-date-picker
            v-model:value="formState.birthdayDate"
            format="DD-MM-YYYY"
            placeholder="Seleccione fecha"
            :style="{ width: '100%' }"
          />
        </a-form-item>
      </a-form>
    </a-spin>

    <template #footer>
      <a-button :disabled="loading" @click="closeModal">Cancelar</a-button>
      <a-button
        type="primary"
        :loading="loading"
        :disabled="saveDisabled"
        @click="saveChanges"
      >
        Guardar
      </a-button>
    </template>
  </a-modal>
</template>

<script setup>
import {
  computed,
  reactive,
  ref,
  nextTick,
  defineEmits,
  defineExpose
} from 'vue';
import { useStore } from 'vuex';
import moment from 'moment';
import { normalizePicker } from '@/utils/calendarDay';
import notification from '@/utils/notifications';
import rutToolBox from '@/utils/Ruts';
import { FETCH_DEPENDENT_BY_ID, PATCH_DEPENDENT_BY_ID } from '@/store/types';

const emit = defineEmits(['saved']);

const store = useStore();
const visible = ref(false);
const loading = ref(false);
const formRef = ref();
const dependentId = ref(null);

const formState = reactive({
  rut: '',
  firstName: '',
  secondName: '',
  firstSurname: '',
  secondSurname: '',
  birthdayDate: null
});

const initialFormValue = ref({});

const rules = {
  firstName: [{ required: true, message: 'Ingrese nombre', trigger: 'blur' }],
  firstSurname: [
    { required: true, message: 'Ingrese apellido paterno', trigger: 'blur' }
  ],
  birthdayDate: [
    {
      required: true,
      type: 'object',
      message: 'Seleccione fecha',
      trigger: 'change'
    }
  ]
};

const saveDisabled = computed(() => {
  const currentValue = JSON.stringify({
    firstName: formState.firstName || '',
    secondName: formState.secondName || '',
    firstSurname: formState.firstSurname || '',
    secondSurname: formState.secondSurname || '',
    birthdayDate: formState.birthdayDate
      ? formState.birthdayDate.format('YYYY-MM-DD')
      : null
  });
  return currentValue === JSON.stringify(initialFormValue.value);
});

const pick = (...values) => values.find((value) => value !== undefined);

const setForm = (dependent) => {
  formState.rut = rutToolBox.formatRut(
    pick(dependent?.rut, dependent?.RUT) || ''
  );
  formState.firstName = pick(dependent?.firstName, dependent?.first_name, '');
  formState.secondName = pick(
    dependent?.secondName,
    dependent?.second_name,
    ''
  );
  formState.firstSurname = pick(
    dependent?.firstSurname,
    dependent?.first_surname,
    ''
  );
  formState.secondSurname = pick(
    dependent?.secondSurname,
    dependent?.second_surname,
    ''
  );

  const birthday = pick(
    dependent?.birthdayDate,
    dependent?.birthday_date,
    null
  );
  // parseZone keeps the stored instant's offset so the displayed birthday
  // (and every later format/normalizePicker read) is host-independent.
  formState.birthdayDate = birthday
    ? moment.parseZone(normalizePicker(birthday))
    : null;

  initialFormValue.value = {
    firstName: formState.firstName || '',
    secondName: formState.secondName || '',
    firstSurname: formState.firstSurname || '',
    secondSurname: formState.secondSurname || '',
    birthdayDate: formState.birthdayDate
      ? formState.birthdayDate.format('YYYY-MM-DD')
      : null
  };
};

const openModal = async (record) => {
  const id = record?.dependentId || record?.id;
  if (!id) {
    notification(
      'error',
      'Error',
      'No se encontró el identificador de la carga'
    );
    return;
  }

  dependentId.value = id;
  visible.value = true;
  loading.value = true;

  try {
    const response = await store.dispatch(FETCH_DEPENDENT_BY_ID, id);
    const dependent = response?.dependent || response?.data || response;
    setForm(dependent || {});
    await nextTick();
    formRef.value?.clearValidate?.();
  } catch (error) {
    notification('error', 'Error', error?.response?.data?.message);
    closeModal(true);
  } finally {
    loading.value = false;
  }
};

const closeModal = (force = false) => {
  if (loading.value && !force) return;
  visible.value = false;
  dependentId.value = null;
  formRef.value?.resetFields?.();
};

const saveChanges = async () => {
  try {
    await formRef.value.validate();
    loading.value = true;

    const payload = {
      firstName: formState.firstName?.trim(),
      secondName: formState.secondName?.trim() || '',
      firstSurname: formState.firstSurname?.trim(),
      secondSurname: formState.secondSurname?.trim() || '',
      birthdayDate: formState.birthdayDate.format('YYYY-MM-DD')
    };

    await store.dispatch(PATCH_DEPENDENT_BY_ID, {
      dependentId: dependentId.value,
      payload
    });

    notification(
      'success',
      'Correcto',
      'Datos personales de la carga actualizados'
    );
    emit('saved');
    closeModal(true);
  } catch (error) {
    if (error?.errorFields) return;
    notification('error', 'Error', error?.response?.data?.message);
  } finally {
    loading.value = false;
  }
};

defineExpose({ openModal });
</script>
